import { __rest } from "tslib";
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { st, classes } from './Input.st.css.js';
import { InputContext } from './InputContext';
import { SIZES } from './constants';
import { STATUS } from '../StatusIndicator/constants.js';
import Ticker from './Ticker';
import IconAffix from './IconAffix';
import Affix from './Affix';
import Group from './Group';
import InputSuffix, { getVisibleSuffixCount } from './InputSuffix';
import { WixStyleReactMaskingContext } from '../WixStyleReactMaskingProvider/context';
import { WixStyleReactDefaultsOverrideContext } from '../WixStyleReactDefaultsOverrideProvider';
import { StatusContext, getStatusFromContext, getAriaAttributesFromContext, } from '../FormField/StatusContext';
const clearButtonSizeMap = {
    [SIZES.small]: 'small',
    [SIZES.medium]: 'medium',
    [SIZES.large]: 'medium',
};
class Input extends Component {
    constructor(props) {
        super(props);
        this._onCompositionChange = isComposing => {
            if (this.props.onCompositionChange) {
                this.props.onCompositionChange(isComposing);
            }
            this.isComposing = isComposing;
        };
        this._extractRef = ref => {
            const { inputRef } = this.props;
            this.input = ref;
            if (inputRef) {
                inputRef(ref);
            }
        };
        this._handleSuffixOnClear = event => {
            const { focusOnClearClick } = this.props;
            focusOnClearClick && this.focus();
            this.clear(event);
        };
        this._onFocus = event => {
            const { onFocus } = this.props;
            this._isMounted && this.setState({ focus: true });
            onFocus && onFocus(event);
            if (this.props.autoSelect) {
                // Set timeout is needed here since onFocus is called before react
                // gets the reference for the input (specifically when autoFocus
                // is on. So setTimeout ensures we have the ref.input needed in select)
                setTimeout(() => {
                    /**
                      here we trying to cover edge case with chrome forms autofill,
                      after user will trigger chrome form autofill, onFocus will be called for each input,
                      each input will cause this.select, select may(mostly all time) cause new onFocus,
                      which will cause new this.select, ..., we have recursion which will all time randomly cause
                      inputs to become focused.
                      To prevent this, we check, that current input node is equal to focused node.
                    */
                    if (document && document.activeElement === this.input) {
                        this.select();
                    }
                }, 0);
            }
        };
        this._onBlur = event => {
            this.setState({ focus: false });
            if (this.props.onBlur) {
                this.props.onBlur(event);
            }
        };
        this._onClick = event => {
            this.props.onInputClicked && this.props.onInputClicked(event);
        };
        this._onKeyDown = event => {
            if (this.isComposing) {
                return;
            }
            const { onKeyDown, onEnterPressed, onEscapePressed } = this.props;
            // On key event
            onKeyDown && onKeyDown(event);
            // Enter
            if (event.key === 'Enter' || event.keyCode === 13) {
                onEnterPressed && onEnterPressed(event);
            }
            // Escape
            if (event.key === 'Escape' || event.keyCode === 27) {
                onEscapePressed && onEscapePressed(event);
            }
        };
        this._isValidInput = value => {
            const { type } = this.props;
            if (type === 'number') {
                /*
                 * Limit our number input to contain only:
                 * - \d - digits
                 * - .  - a dot
                 * - ,  - a comma
                 * - \- - a hyphen minus
                 * - +  - a plus sign
                 */
                return /^[\d.,\-+]*$/.test(value);
            }
            return true;
        };
        this._onChange = event => {
            const { onChange } = this.props;
            if (this._isValidInput(event.target.value)) {
                onChange && onChange(event);
            }
        };
        this._onKeyPress = event => {
            if (!this._isValidInput(event.target.value + event.key)) {
                event.preventDefault();
            }
        };
        this._renderInput = props => {
            const { customInput: CustomInputComponent, ref } = props, rest = __rest(props, ["customInput", "ref"]);
            const inputProps = Object.assign({}, (CustomInputComponent
                ? Object.assign(Object.assign({ ref }, rest), { 'data-hook': 'wsr-custom-input' }) : Object.assign({ ref }, rest)));
            return React.cloneElement(CustomInputComponent ? React.createElement(CustomInputComponent, null) : React.createElement("input", null), inputProps);
        };
        this._shouldShowStatusSuffix = ({ finalStatus, statusFromProp }) => !this.props.hideStatusSuffix &&
            (!!statusFromProp || finalStatus === 'loading') &&
            Object.values(STATUS).includes(finalStatus);
        /**
         * Sets focus on the input element
         * @param {FocusOptions} options
         */
        this.focus = (options = {}) => {
            this.input && this.input.focus(options);
        };
        /**
         * Removes focus on the input element
         */
        this.blur = () => {
            this.input && this.input.blur();
        };
        /**
         * Selects all text in the input element
         */
        this.select = () => {
            this.input && this.input.select();
        };
        /**
         * Sets the start and end positions of the current text selection in the input element
         */
        this.setSelectionRange = (start, end) => {
            this.input && this.input.setSelectionRange(start, end);
        };
        /**
         * Clears the input.
         * Fires onClear with the given event triggered on the clear button
         *
         * @param event delegated to the onClear call
         */
        this.clear = event => {
            const { onClear } = this.props;
            if (!this._isControlled) {
                this.input.value = '';
            }
            onClear && onClear(event);
        };
        this._isMounted = false;
        this.state = {
            focus: false,
        };
    }
    componentDidMount() {
        this._isMounted = true;
        const { autoFocus, value } = this.props;
        autoFocus && this._onFocus();
        /*
         * autoFocus doesn't automatically selects text like focus do.
         * Therefore we set the selection range, but in order to support prior implementation we set the start position as the end in order to place the cursor there.
         */
        if (autoFocus && !!value) {
            this.input.setSelectionRange(value.length, value.length);
        }
    }
    componentWillUnmount() {
        this._isMounted = false;
    }
    get _isClearFeatureEnabled() {
        const { onClear, clearButton } = this.props;
        return !!onClear || !!clearButton;
    }
    get _isControlled() {
        const { value } = this.props;
        return value !== undefined;
    }
    render(props = {}) {
        const { input: inputPropsDefaults } = this.context;
        const { id, name, value, placeholder, menuArrow, defaultValue, tabIndex, autoFocus, onKeyUp, onPaste, disableEditing, readOnly, prefix, suffix, type, maxLength, textOverflow, disabled, status: statusProp, statusMessage, tooltipPlacement, autocomplete, min, max, step, required, customInput, pattern, size = inputPropsDefaults.size, clearButtonTooltipContent, clearButtonTooltipProps, } = this.props;
        const onIconClicked = event => {
            if (!disabled) {
                this.input && this.input.focus();
                this._onClick(event);
            }
        };
        // this doesn't work for uncontrolled, "value" refers only to controlled input
        const isClearButtonVisible = this._isClearFeatureEnabled && !!value && !disabled;
        // Aria Attributes
        const shouldRenderSuffix = ({ finalStatus, statusFromProp }) => {
            const visibleSuffixCount = getVisibleSuffixCount({
                status: this._shouldShowStatusSuffix({
                    finalStatus,
                    statusFromProp,
                }),
                statusMessage,
                disabled,
                isClearButtonVisible,
                menuArrow,
                suffix,
            });
            return visibleSuffixCount > 0;
        };
        const ariaAttribute = {};
        Object.keys(this.props)
            .filter(key => key.startsWith('aria'))
            .map(key => (ariaAttribute['aria-' + key.substr(4).toLowerCase()] =
            this.props[key]));
        /* eslint-disable no-unused-vars */
        const { className } = props, inputElementProps = __rest(props, ["className"]);
        const inputElement = (React.createElement(WixStyleReactMaskingContext.Consumer, null, ({ maskingClassNames }) => (React.createElement(StatusContext.Consumer, null, statusContext => this._renderInput(Object.assign(Object.assign(Object.assign({ min,
            max,
            step, 'data-hook': 'wsr-input', 'data-mask': !!maskingClassNames, style: { textOverflow }, ref: this._extractRef, className: st(classes.input, {}, maskingClassNames), id,
            name,
            disabled,
            defaultValue,
            value, onChange: this._onChange, onKeyPress: this._onKeyPress, maxLength, onFocus: this._onFocus, onBlur: this._onBlur, onWheel: () => {
                // Although it's opposed to the native behavior, we decided to blur an input type="number" on wheel event in order to prevent change in value.
                if (type === 'number')
                    this.blur();
            }, onKeyDown: this._onKeyDown, onPaste,
            placeholder,
            tabIndex,
            autoFocus, onClick: this._onClick, onKeyUp, readOnly: readOnly || disableEditing, type,
            required, autoComplete: autocomplete, onCompositionStart: () => this._onCompositionChange(true), onCompositionEnd: () => this._onCompositionChange(false), customInput,
            pattern }, getAriaAttributesFromContext(statusContext)), ariaAttribute), inputElementProps))))));
        return (React.createElement("div", { className: classes.wrapper },
            prefix && (React.createElement(InputContext.Provider, { value: Object.assign(Object.assign({}, this.props), { size, inPrefix: true }) }, prefix)),
            inputElement,
            React.createElement(InputContext.Provider, { value: Object.assign(Object.assign({}, this.props), { size, inSuffix: true }) },
                React.createElement(StatusContext.Consumer, null, statusContext => {
                    const status = getStatusFromContext(statusContext, statusProp);
                    return (shouldRenderSuffix({
                        finalStatus: status,
                        statusFromProp: statusProp,
                    }) && (React.createElement(InputSuffix, { status: this._shouldShowStatusSuffix({
                            finalStatus: status,
                            statusFromProp: statusProp,
                        })
                            ? status
                            : undefined, statusMessage: statusMessage, disabled: disabled, onIconClicked: onIconClicked, isClearButtonVisible: isClearButtonVisible, onClear: this._handleSuffixOnClear, clearButtonSize: clearButtonSizeMap[size], menuArrow: menuArrow, suffix: suffix, tooltipPlacement: tooltipPlacement, clearButtonTooltipContent: clearButtonTooltipContent, clearButtonTooltipProps: clearButtonTooltipProps })));
                }))));
    }
}
Input.contextType = WixStyleReactDefaultsOverrideContext;
Input.Ticker = Ticker;
Input.IconAffix = IconAffix;
Input.Affix = Affix;
Input.Group = Group;
Input.StatusError = STATUS.ERROR;
Input.StatusWarning = STATUS.WARNING;
Input.StatusLoading = STATUS.LOADING;
Input.displayName = 'Input';
Input.defaultProps = {
    focusOnClearClick: true,
    autoSelect: true,
    border: 'standard',
    textOverflow: 'clip',
    maxLength: 524288,
    clearButton: false,
    hideStatusSuffix: false,
};
Input.propTypes = {
    /** Applies a data-hook HTML attribute that can be used in the tests */
    dataHook: PropTypes.string,
    /** Specifies a CSS class name to be appended to the component’s root element */
    className: PropTypes.string,
    /** Assigns a unique identifier for the root element */
    id: PropTypes.string,
    /** Associate a control with the regions that it controls */
    ariaControls: PropTypes.string,
    /** Associate a region with its descriptions. Similar to aria-controls but instead associating descriptions to the region and description identifiers are separated with a space. */
    ariaDescribedby: PropTypes.string,
    /** Define a string that labels the current element in case where a text label is not visible on the screen */
    ariaLabel: PropTypes.string,
    /** Focus the element on mount (standard React input autoFocus) */
    autoFocus: PropTypes.bool,
    /** Sets the value of native autocomplete attribute (check the [HTML spec](https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#attr-fe-autocomplete) for possible values  */
    autocomplete: PropTypes.string,
    /** Defines the initial value of an input */
    defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** Specifies whether input should be disabled or not */
    disabled: PropTypes.bool,
    /** Specify the status of a field */
    status: PropTypes.oneOf(['error', 'warning', 'loading']),
    /** Defines the message to display on status icon hover. If not given or empty there will be no tooltip. */
    statusMessage: PropTypes.node,
    /** Specifies whether status suffix should be hidden */
    hideStatusSuffix: PropTypes.bool,
    /** USED FOR TESTING - forces focus state on the input */
    forceFocus: PropTypes.bool,
    /** USED FOR TESTING - forces hover state on the input */
    forceHover: PropTypes.bool,
    /** Sets the maximum number of characters that can be inserted to a field */
    maxLength: PropTypes.number,
    /** Specifies whether input should have a dropdown menu arrow on the right side */
    menuArrow: PropTypes.bool,
    /** Displays clear button (X) on a non-empty input */
    clearButton: PropTypes.bool,
    /** Reference element data when a form is submitted */
    name: PropTypes.string,
    /** Control the border style of input */
    border: PropTypes.oneOf(['standard', 'round', 'bottomLine', 'none']),
    /** Specifies whether input shouldn’t have rounded corners on its left */
    noLeftBorderRadius: PropTypes.bool,
    /** Specifies whether input shouldn’t have rounded corners on its right */
    noRightBorderRadius: PropTypes.bool,
    /** Defines a standard input onBlur callback */
    onBlur: PropTypes.func,
    /** Defines a standard input onChange callback */
    onChange: PropTypes.func,
    /** Displays clear button (X) on a non-empty input and calls a callback function with no arguments */
    onClear: PropTypes.func,
    /** Defines a callback function called on compositionstart/compositionend events */
    onCompositionChange: PropTypes.func,
    /** Defines a callback handler that is called when the user presses -enter- */
    onEnterPressed: PropTypes.func,
    /** Defines a callback handler that is called when the user presses -escape- */
    onEscapePressed: PropTypes.func,
    /** Defines a standard input onFocus callback */
    onFocus: PropTypes.func,
    /** Defines a standard input onClick callback */
    onInputClicked: PropTypes.func,
    /** Defines a standard input onKeyDown callback */
    onKeyDown: PropTypes.func,
    /** Defines a standard input onKeyUp callback */
    onKeyUp: PropTypes.func,
    /** Defines a callback handler that is called when user pastes text from a clipboard (using a mouse or keyboard shortcut) */
    onPaste: PropTypes.func,
    /** Sets a placeholder message to display */
    placeholder: PropTypes.string,
    /** Pass a component you want to show as the prefix of the input, e.g., text, icon */
    prefix: PropTypes.node,
    /** Specifies whether input is read only */
    readOnly: PropTypes.bool,
    /** Restricts input editing */
    disableEditing: PropTypes.bool,
    /** Controls the size of the input. Default value: `medium` */
    size: PropTypes.oneOf(['tiny', 'small', 'medium', 'large']),
    /** Pass a component you want to show as the suffix of the input, e.g., text, icon */
    suffix: PropTypes.node,
    /** Indicates that element can be focused and where it participates in sequential keyboard navigation */
    tabIndex: PropTypes.number,
    /** Handles text overflow behavior. It can either `clip` (default) or display `ellipsis`. */
    textOverflow: PropTypes.string,
    /** Controls placement of a status tooltip */
    tooltipPlacement: PropTypes.string,
    /** Specifies the type of `<input/>` element to display. Default is text. */
    type: PropTypes.string,
    /** Specifies the current value of the element */
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** Specifies whether input is mandatory */
    required: PropTypes.bool,
    /** Sets a minimum value of an input. Similar to HTML5 min attribute. */
    min: PropTypes.number,
    /** Sets a maximum value of an input. Similar to html5 max attribute. */
    max: PropTypes.number,
    /** Specifies the interval between number values */
    step: PropTypes.number,
    /** Render a custom input instead of the default html input tag */
    customInput: PropTypes.elementType
        ? PropTypes.oneOfType([
            PropTypes.func,
            PropTypes.node,
            PropTypes.elementType,
        ])
        : PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
    /** Sets a pattern that typed value must match to be valid (regex) */
    pattern: PropTypes.string,
    /** Specifies whether to focus the field when clear button is clicked */
    focusOnClearClick: PropTypes.bool,
    /** When provided hover will display a tooltip with content */
    clearButtonTooltipContent: PropTypes.node,
    /** Clear button tooltip props */
    clearButtonTooltipProps: PropTypes.node,
};
export default Input;
//# sourceMappingURL=Input.js.map